import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { ActivityLogTimeline } from "./ActivityLogTimeline";
import { getContentTypeLabel } from "@/utils/ab-test-utils";
import type { ABTest } from "@/types/ab-tests";

interface EditCompletedTestViewProps {
  test: ABTest;
  productId: number | string | null;
  brandId: number | null;
  onReuploadResults: () => void;
  onClose: () => void;
}

export function EditCompletedTestView({
  test,
  productId,
  brandId,
  onReuploadResults,
  onClose,
}: EditCompletedTestViewProps) {
  const numericProductId =
    typeof productId === "string"
      ? Number(productId.replace(/^hero-/i, ""))
      : typeof productId === "number"
      ? productId
      : undefined;

  return (
    <>
      <div className="space-y-4 py-2">
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Test Type</p>
              <p className="text-sm text-foreground">{test.testType}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Scope</p>
              <p className="text-sm text-foreground">
                {(test.scopeFields || [test.contentType]).map(f => getContentTypeLabel(f)).join(", ")}
              </p>
            </div>
          </div>
          {test.targetKeyword && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Target Keyword</p>
              <p className="text-sm text-foreground">{test.targetKeyword}</p>
            </div>
          )}
        </div>

        {test.hypothesis && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Hypothesis</p>
            <p className="text-sm text-foreground leading-relaxed">{test.hypothesis}</p>
          </div>
        )}

        {test.outcome && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Outcome</p>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-foreground">
                {test.outcome === "variant" && "Apply Variant B"}
                {test.outcome === "control" && "Keep Variant A (control)"}
                {test.outcome === "inconclusive" && "Inconclusive"}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Amazon Experiment Results</p>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onReuploadResults}>
              <Upload className="h-3 w-3 mr-1" />Re-upload
            </Button>
          </div>
          {test.amazonMetrics ? (
            <div className="bg-muted/50 rounded-md p-3 text-sm space-y-1">
              {test.amazonMetrics.conversion && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Conversion</span>
                  <span className="tabular-nums">{test.amazonMetrics.conversion.a} / {test.amazonMetrics.conversion.b}</span>
                </div>
              )}
              {test.amazonMetrics.unitsSold && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Units Sold</span>
                  <span className="tabular-nums">
                    {typeof test.amazonMetrics.unitsSold.a === "number"
                      ? test.amazonMetrics.unitsSold.a.toLocaleString()
                      : test.amazonMetrics.unitsSold.a}{" "}
                    /{" "}
                    {typeof test.amazonMetrics.unitsSold.b === "number"
                      ? test.amazonMetrics.unitsSold.b.toLocaleString()
                      : test.amazonMetrics.unitsSold.b}
                  </span>
                </div>
              )}
              {test.amazonMetrics.sales && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Sales</span>
                  <span className="tabular-nums">{test.amazonMetrics.sales.a} / {test.amazonMetrics.sales.b}</span>
                </div>
              )}
              {test.amazonMetrics.sampleSize && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Sample Size</span>
                  <span className="tabular-nums">
                    {typeof test.amazonMetrics.sampleSize.a === "number"
                      ? test.amazonMetrics.sampleSize.a.toLocaleString()
                      : test.amazonMetrics.sampleSize.a}{" "}
                    /{" "}
                    {typeof test.amazonMetrics.sampleSize.b === "number"
                      ? test.amazonMetrics.sampleSize.b.toLocaleString()
                      : test.amazonMetrics.sampleSize.b}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No metrics uploaded yet.</p>
          )}
        </div>
      </div>

      <div className="border-t pt-4">
        <ActivityLogTimeline
          entries={test.activityLog || []}
          showHeader={true}
          productId={numericProductId}
          brandId={brandId ?? undefined}
          testId={test.id}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );
}
